import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CompanyDetailsModal = ({ isOpen, onClose, company }) => {
  if (!isOpen || !company) return null;

  const getSubscriptionBadge = (tier) => {
    const badges = {
      'Starter': 'bg-info/10 text-info border-info/20',
      'Professional': 'bg-primary/10 text-primary border-primary/20',
      'Enterprise': 'bg-warning/10 text-warning border-warning/20'
    };
    return badges?.[tier] || 'bg-muted text-muted-foreground border-border';
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">Company Details</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Building2" size={32} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-xl text-foreground">{company?.businessName}</h3>
                <p className="text-sm text-muted-foreground">{company?.legalName}</p>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium border ${getSubscriptionBadge(company?.subscriptionTier)}`}>
                    {company?.subscriptionTier} Plan
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Business Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon name="Hash" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Tax ID:</span>
                    <span className="text-sm text-foreground font-medium">{company?.taxId || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Active Users:</span>
                    <span className="text-sm text-foreground font-medium">{company?.activeUsers || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Shield" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Data Isolation:</span>
                    <span className="text-sm text-foreground font-medium">{company?.dataIsolation ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Contact Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{company?.contactPerson}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{company?.contactEmail}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Phone" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{company?.contactPhone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Address</h4>
              <div className="flex items-start gap-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground mt-1" />
                <div className="text-sm text-foreground">
                  {company?.address && <p>{company?.address}</p>}
                  {(company?.city || company?.state || company?.zipCode) && (
                    <p>{company?.city}{company?.city && company?.state ? ', ' : ''}{company?.state} {company?.zipCode}</p>
                  )}
                  {company?.country && <p>{company?.country}</p>}
                  {!company?.address && !company?.city && !company?.state && !company?.zipCode && !company?.country && (
                    <p className="text-muted-foreground">No address provided</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Subscription Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Current Plan</p>
                  <p className="text-sm text-foreground font-medium">{company?.subscriptionTier}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Billing Status</p>
                  <p className="text-sm text-success font-medium">Active</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Next Billing Date</p>
                  <p className="text-sm text-foreground font-medium">Feb 28, 2026</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Monthly Cost</p>
                  <p className="text-sm text-foreground font-medium">
                    {company?.subscriptionTier === 'Starter' ? '$29' : company?.subscriptionTier === 'Professional' ? '$99' : 'Custom'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsModal;
